import React, { useState, useEffect } from "react";
import {
  Table,
  Button,
  Input,
  Modal,
  Form,
  Checkbox,
  Typography,
  message,
  Flex,
  Dropdown,
  Menu,
  Select,
  Tag,
  Popconfirm,
  Space,
  Card,
  Row,
  Col,
  Empty,
  Spin,
  Collapse,
} from "antd";
import { 
  MoreOutlined, SearchOutlined, PlusOutlined, EditOutlined, 
  DeleteOutlined, CheckCircleOutlined, CloseCircleOutlined,
  TeamOutlined, SafetyCertificateOutlined, SettingOutlined
} from "@ant-design/icons";
import { RolePermissionWrapper, PageHeader, TabsContainer } from "./RolePermission.style";
import axios from "axios";
import { API_CONST } from "../../../const";
import dayjs from "dayjs";

const { Text, Title } = Typography;
const { Option } = Select;
const { Panel } = Collapse;

const RolePermission = () => {
  const [activeTab, setActiveTab] = useState("ProfileRoles");
  const [loading, setLoading] = useState(false);
  const [permissions, setPermissions] = useState([]);
  const [profileRoles, setProfileRoles] = useState([]);
  const [permissionModules, setPermissionModules] = useState([]);
  const [searchText, setSearchText] = useState("");
  const [parentRoleFilter, setParentRoleFilter] = useState("");
  
  const [isRoleModalVisible, setIsRoleModalVisible] = useState(false);
  const [isPermissionModalVisible, setIsPermissionModalVisible] = useState(false);
  const [isProfileRoleModalVisible, setIsProfileRoleModalVisible] = useState(false);
  const [editingProfileRole, setEditingProfileRole] = useState(null);
  const [editingPermission, setEditingPermission] = useState(null);
  
  const [roleForm] = Form.useForm();
  const [permissionForm] = Form.useForm();
  const [profileRoleForm] = Form.useForm();
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (activeTab === "Permissions") {
      fetchPermissions();
    } else if (activeTab === "ProfileRoles") {
      fetchProfileRoles();
      fetchPermissionsForSelection();
    }
  }, [activeTab, parentRoleFilter]);

  const fetchPermissions = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("authToken");
      const response = await axios.post(
        API_CONST.GET_PERMISSIONS,
        { parentRole: parentRoleFilter || undefined, search: searchText || undefined },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (response.data.success) {
        setPermissions(response.data.permissions);
      }
    } catch (error) {
      console.error("Error fetching permissions:", error);
      message.error("Failed to fetch permissions");
    } finally {
      setLoading(false);
    }
  };

  const fetchPermissionsForSelection = async () => {
    try {
      const token = localStorage.getItem("authToken");
      const selectedParentRole = profileRoleForm.getFieldValue("parentRole") || parentRoleFilter || undefined;
      const parentRole = selectedParentRole === "Admin" ? undefined : selectedParentRole;
      const response = await axios.post(
        API_CONST.GET_PERMISSIONS,
        { parentRole, isActive: true },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (response.data.success) {
        setPermissions(response.data.permissions);
        const modules = [...new Set(response.data.permissions.map(p => p.module))];
        setPermissionModules(modules);
      }
    } catch (error) {
      console.error("Error fetching permissions:", error);
    }
  };

  const fetchProfileRoles = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("authToken");
      const response = await axios.post(
        API_CONST.GET_PROFILE_ROLES,
        { 
          parentRole: parentRoleFilter || undefined, 
          scope: "global",
          search: searchText || undefined 
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (response.data.success) {
        setProfileRoles(response.data.profileRoles);
      }
    } catch (error) {
      console.error("Error fetching profile roles:", error);
      message.error("Failed to fetch profile roles");
    } finally {
      setLoading(false);
    }
  };

  const handleSeedPermissions = async () => {
    try {
      const token = localStorage.getItem("authToken");
      const response = await axios.post(
        API_CONST.SEED_PERMISSIONS,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (response.data.success) {
        message.success("Permissions and profile roles seeded successfully!");
        fetchPermissions();
        fetchProfileRoles();
      }
    } catch (error) {
      message.error(error.response?.data?.error || "Failed to seed permissions");
    }
  };

  const handleSearch = () => {
    if (activeTab === "Permissions") {
      fetchPermissions();
    } else {
      fetchProfileRoles();
    }
  };

  const handleOpenPermissionModal = (record = null) => {
    setEditingPermission(record);
    if (record) {
      permissionForm.setFieldsValue(record);
    } else {
      permissionForm.resetFields();
    }
    setIsPermissionModalVisible(true);
  };

  const handleClosePermissionModal = () => {
    setIsPermissionModalVisible(false);
    setEditingPermission(null);
    permissionForm.resetFields();
  };

  const handlePermissionSubmit = async (values) => {
    setSaving(true);
    try {
      const token = localStorage.getItem("authToken");
      if (editingPermission) {
        await axios.put(
          `${API_CONST.UPDATE_PERMISSION}/${editingPermission.id}`,
          values,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        message.success("Permission updated successfully");
      } else {
        await axios.post(
          API_CONST.CREATE_PERMISSION,
          values,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        message.success("Permission created successfully");
      }
      handleClosePermissionModal();
      fetchPermissions();
    } catch (error) {
      message.error(error.response?.data?.error || "Failed to save permission");
    } finally {
      setSaving(false);
    }
  };

  const handleDeletePermission = async (id) => {
    try {
      const token = localStorage.getItem("authToken");
      await axios.delete(
        `${API_CONST.DELETE_PERMISSION}/${id}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      message.success("Permission deleted");
      fetchPermissions();
    } catch (error) {
      message.error(error.response?.data?.error || "Failed to delete");
    }
  };

  const handleOpenProfileRoleModal = (record = null) => {
    setEditingProfileRole(record);
    if (record) {
      profileRoleForm.setFieldsValue({
        ...record,
        selectedPermissions: record.permissions || [],
      });
    } else {
      profileRoleForm.resetFields();
      profileRoleForm.setFieldValue("scope", "global");
    }
    fetchPermissionsForSelection();
    setIsProfileRoleModalVisible(true);
  };

  const handleCloseProfileRoleModal = () => {
    setIsProfileRoleModalVisible(false);
    setEditingProfileRole(null);
    profileRoleForm.resetFields();
  };

  const handleProfileRoleSubmit = async (values) => {
    setSaving(true);
    try {
      const token = localStorage.getItem("authToken");
      const payload = {
        name: values.name,
        description: values.description,
        parentRole: values.parentRole,
        scope: values.scope || "global",
        permissions: values.selectedPermissions || [],
        isDefault: values.isDefault || false,
      };
      
      if (editingProfileRole) {
        await axios.put(
          `${API_CONST.UPDATE_PROFILE_ROLE}/${editingProfileRole.id}`,
          payload,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        message.success("Profile role updated successfully");
      } else {
        await axios.post(
          API_CONST.CREATE_PROFILE_ROLE,
          payload,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        message.success("Profile role created successfully");
      }
      handleCloseProfileRoleModal();
      fetchProfileRoles();
    } catch (error) {
      message.error(error.response?.data?.error || "Failed to save profile role");
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteProfileRole = async (id) => {
    try {
      const token = localStorage.getItem("authToken");
      await axios.delete(
        `${API_CONST.DELETE_PROFILE_ROLE}/${id}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      message.success("Profile role deleted");
      fetchProfileRoles();
    } catch (error) {
      message.error(error.response?.data?.error || "Failed to delete");
    }
  };

  const handleParentRoleChange = (value) => {
    profileRoleForm.setFieldValue("selectedPermissions", []);
    fetchPermissionsForSelection();
  };

  const getPermissionsByModule = () => {
    const grouped = {};
    permissions.forEach(p => {
      if (!grouped[p.module]) {
        grouped[p.module] = [];
      }
      grouped[p.module].push(p);
    });
    return grouped;
  };

  const permissionColumns = [
    {
      title: "Code",
      dataIndex: "code",
      key: "code",
      width: 180,
      render: (code) => <code style={{ fontSize: 12 }}>{code}</code>
    },
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
    },
    {
      title: "Module",
      dataIndex: "module",
      key: "module",
      width: 180,
      render: (m) => <Tag color="blue">{m}</Tag>
    },
    {
      title: "Parent Role",
      dataIndex: "parentRole",
      key: "parentRole",
      width: 120,
      render: (r) => {
        const colors = { Customer: "green", Partner: "purple", Admin: "red", All: "default" };
        return <Tag color={colors[r] || "default"}>{r}</Tag>;
      }
    },
    {
      title: "Active",
      dataIndex: "isActive",
      key: "isActive",
      width: 80,
      render: (active) => active 
        ? <CheckCircleOutlined style={{ color: "#52c41a" }} />
        : <CloseCircleOutlined style={{ color: "#ff4d4f" }} />
    },
    {
      title: "Action",
      key: "action",
      width: 100,
      render: (_, record) => (
        <Space size="small">
          <Button type="link" size="small" icon={<EditOutlined />} onClick={() => handleOpenPermissionModal(record)} />
          <Popconfirm title="Delete this permission?" onConfirm={() => handleDeletePermission(record.id)}>
            <Button type="link" size="small" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  const profileRoleColumns = [
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
      render: (name, record) => (
        <div>
          <div style={{ fontWeight: 500 }}>{name}</div>
          {record.isDefault && <Tag color="gold" style={{ fontSize: 10 }}>DEFAULT</Tag>}
        </div>
      )
    },
    {
      title: "Description",
      dataIndex: "description",
      key: "description",
      ellipsis: true,
    },
    {
      title: "Parent Role",
      dataIndex: "parentRole",
      key: "parentRole",
      width: 120,
      render: (r) => {
        const colors = { Customer: "green", Partner: "purple", Admin: "red" };
        return <Tag color={colors[r] || "default"}>{r}</Tag>;
      }
    },
    {
      title: "Scope",
      dataIndex: "scope",
      key: "scope",
      width: 120,
      render: (s) => <Tag color={s === "global" ? "cyan" : "orange"}>{s.toUpperCase()}</Tag>
    },
    {
      title: "Permissions",
      dataIndex: "permissions",
      key: "permissions",
      width: 120,
      render: (perms) => <Tag>{perms?.length || 0} permissions</Tag>
    },
    {
      title: "Active",
      dataIndex: "isActive",
      key: "isActive",
      width: 80,
      render: (active) => active 
        ? <CheckCircleOutlined style={{ color: "#52c41a" }} />
        : <CloseCircleOutlined style={{ color: "#ff4d4f" }} />
    },
    {
      title: "Action",
      key: "action",
      width: 100,
      render: (_, record) => (
        <Space size="small">
          <Button type="link" size="small" icon={<EditOutlined />} onClick={() => handleOpenProfileRoleModal(record)} />
          <Popconfirm title="Delete this profile role?" onConfirm={() => handleDeleteProfileRole(record.id)}>
            <Button type="link" size="small" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <RolePermissionWrapper>
      <PageHeader><h1>Roles & Permissions</h1><p>Manage roles and configure access permissions</p></PageHeader>
      
      <TabsContainer>
        <Button
          className={activeTab === "ProfileRoles" ? "tab-button active" : "tab-button"}
          onClick={() => setActiveTab("ProfileRoles")}
        >
          <TeamOutlined /> Profile Roles
        </Button>
        <Button
          className={activeTab === "Permissions" ? "tab-button active" : "tab-button"}
          onClick={() => setActiveTab("Permissions")}
        >
          <SafetyCertificateOutlined /> Permissions
        </Button>
      </TabsContainer>

      {activeTab === "ProfileRoles" && (
        <>
          <Flex align="center" justify="space-between" style={{ marginBottom: 16 }}>
            <Space>
              <Input
                prefix={<SearchOutlined />}
                placeholder="Search profile roles"
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                onPressEnter={handleSearch}
                style={{ width: 250 }}
              />
              <Select
                placeholder="Filter by Parent Role"
                allowClear
                value={parentRoleFilter || undefined}
                onChange={(val) => setParentRoleFilter(val || "")}
                style={{ width: 180 }}
              >
                <Option value="Admin">Admin</Option>
                <Option value="Customer">Customer</Option>
                <Option value="Partner">Partner</Option>
              </Select>
              <Button onClick={handleSearch}>Search</Button>
            </Space>
            <Space>
              <Popconfirm 
                title="Seed Default Permissions?" 
                description="This will add default permissions and profile roles if they don't exist."
                onConfirm={handleSeedPermissions}
              >
                <Button icon={<SettingOutlined />}>Seed Defaults</Button>
              </Popconfirm>
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={() => handleOpenProfileRoleModal()}
                style={{ 
                  backgroundColor: "#00d9a9",
                  borderColor: "#00d9a9",
                }}
              >
                Add Profile Role
              </Button>
            </Space>
          </Flex>

          <Table
            columns={profileRoleColumns}
            dataSource={profileRoles}
            rowKey="id"
            loading={loading}
            pagination={{ pageSize: 10, showSizeChanger: true, pageSizeOptions: ["10", "20", "50", "100"], showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} rows` }}
          />
        </>
      )}

      {activeTab === "Permissions" && (
        <>
          <Flex align="center" justify="space-between" style={{ marginBottom: 16 }}>
            <Space>
              <Input
                prefix={<SearchOutlined />}
                placeholder="Search permissions"
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                onPressEnter={handleSearch}
                style={{ width: 250 }}
              />
              <Select
                placeholder="Filter by Parent Role"
                allowClear
                value={parentRoleFilter || undefined}
                onChange={(val) => setParentRoleFilter(val || "")}
                style={{ width: 180 }}
              >
                <Option value="Customer">Customer</Option>
                <Option value="Partner">Partner</Option>
                <Option value="Admin">Admin</Option>
                <Option value="All">All</Option>
              </Select>
              <Button onClick={handleSearch}>Search</Button>
            </Space>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => handleOpenPermissionModal()}
              style={{ 
                backgroundColor: "#00d9a9",
                borderColor: "#00d9a9",
              }}
            >
              Add Permission
            </Button>
          </Flex>

          <Table
            columns={permissionColumns}
            dataSource={permissions}
            rowKey="id"
            loading={loading}
            pagination={{ pageSize: 15, showSizeChanger: true, pageSizeOptions: ["10", "20", "50", "100"], showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} rows` }}
          />
        </>
      )}

      <Modal
        title={editingPermission ? "Edit Permission" : "Add New Permission"}
        open={isPermissionModalVisible}
        onCancel={handleClosePermissionModal}
        footer={null}
        width={600}
      >
        <Form form={permissionForm} layout="vertical" onFinish={handlePermissionSubmit}>
          {!editingPermission ? (
            <>
              <div style={{ marginBottom: 16, padding: 12, background: "#f6ffed", borderRadius: 6, border: "1px solid #b7eb8f" }}>
                <Text type="secondary">Select a module and action to auto-generate the permission code</Text>
              </div>
              <Row gutter={16}>
                <Col span={12}>
                  <Form.Item
                    label="Module"
                    name="moduleKey"
                    rules={[{ required: true, message: "Please select a module" }]}
                  >
                    <Select 
                      placeholder="Select module"
                      onChange={(value) => {
                        const action = permissionForm.getFieldValue("actionKey");
                        if (value && action) {
                          const code = `${value}.${action}`;
                          const moduleNames = {
                            dashboard: "Dashboard", engagement: "Engagements", project: "Projects",
                            job: "Job Requirements", talent: "Talent Management", timesheet: "Timesheets",
                            invoice: "Invoices", report: "Reports", settings: "Settings",
                            team: "Team Management", roles: "Roles & Permissions", assessment: "AI Assessments",
                            issue: "Issues & Feedback", hired: "Hired Talents", bench: "Bench Pool",
                            ongoing_jobs: "Ongoing Jobs", profile: "Profile", approval: "Approvals",
                            user: "User Management", organization: "Organizations", finance: "Finance"
                          };
                          const actionNames = { view: "View", create: "Create", edit: "Edit", delete: "Delete", approve: "Approve", reject: "Reject", submit: "Submit", export: "Export", search: "Search", match: "Match", convert: "Convert", manage: "Manage", invite: "Invite", org_manage: "Organization Manage" };
                          const name = `${actionNames[action] || action} ${moduleNames[value] || value}`;
                          permissionForm.setFieldsValue({ code, name, module: moduleNames[value] || value });
                        }
                      }}
                    >
                      <Option value="dashboard">Dashboard</Option>
                      <Option value="engagement">Engagements</Option>
                      <Option value="project">Projects</Option>
                      <Option value="job">Job Requirements</Option>
                      <Option value="talent">Talent Management</Option>
                      <Option value="timesheet">Timesheets</Option>
                      <Option value="invoice">Invoices</Option>
                      <Option value="report">Reports</Option>
                      <Option value="settings">Settings</Option>
                      <Option value="team">Team Management</Option>
                      <Option value="roles">Roles & Permissions</Option>
                      <Option value="assessment">AI Assessments</Option>
                      <Option value="issue">Issues & Feedback</Option>
                      <Option value="hired">Hired Talents</Option>
                      <Option value="bench">Bench Pool</Option>
                      <Option value="ongoing_jobs">Ongoing Jobs</Option>
                      <Option value="profile">Profile</Option>
                      <Option value="approval">Approvals</Option>
                      <Option value="user">User Management</Option>
                      <Option value="organization">Organizations</Option>
                      <Option value="finance">Finance</Option>
                    </Select>
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    label="Action"
                    name="actionKey"
                    rules={[{ required: true, message: "Please select an action" }]}
                  >
                    <Select 
                      placeholder="Select action"
                      onChange={(value) => {
                        const moduleKey = permissionForm.getFieldValue("moduleKey");
                        if (moduleKey && value) {
                          const code = `${moduleKey}.${value}`;
                          const moduleNames = {
                            dashboard: "Dashboard", engagement: "Engagements", project: "Projects",
                            job: "Job Requirements", talent: "Talent Management", timesheet: "Timesheets",
                            invoice: "Invoices", report: "Reports", settings: "Settings",
                            team: "Team Management", roles: "Roles & Permissions", assessment: "AI Assessments",
                            issue: "Issues & Feedback", hired: "Hired Talents", bench: "Bench Pool",
                            ongoing_jobs: "Ongoing Jobs", profile: "Profile", approval: "Approvals",
                            user: "User Management", organization: "Organizations", finance: "Finance"
                          };
                          const actionNames = { view: "View", create: "Create", edit: "Edit", delete: "Delete", approve: "Approve", reject: "Reject", submit: "Submit", export: "Export", search: "Search", match: "Match", convert: "Convert", manage: "Manage", invite: "Invite", org_manage: "Organization Manage" };
                          const name = `${actionNames[value] || value} ${moduleNames[moduleKey] || moduleKey}`;
                          permissionForm.setFieldsValue({ code, name, module: moduleNames[moduleKey] || moduleKey });
                        }
                      }}
                    >
                      <Option value="view">View</Option>
                      <Option value="create">Create</Option>
                      <Option value="edit">Edit</Option>
                      <Option value="delete">Delete</Option>
                      <Option value="approve">Approve</Option>
                      <Option value="reject">Reject</Option>
                      <Option value="submit">Submit</Option>
                      <Option value="export">Export</Option>
                      <Option value="search">Search</Option>
                      <Option value="match">Match</Option>
                      <Option value="convert">Convert</Option>
                      <Option value="manage">Manage</Option>
                      <Option value="invite">Invite</Option>
                      <Option value="org_manage">Organization Manage</Option>
                    </Select>
                  </Form.Item>
                </Col>
              </Row>
              <Row gutter={16}>
                <Col span={12}>
                  <Form.Item label="Generated Code" name="code">
                    <Input disabled style={{ fontFamily: "monospace", background: "#fafafa" }} />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item label="Auto-Generated Name" name="name">
                    <Input placeholder="Auto-generated from selection" />
                  </Form.Item>
                </Col>
              </Row>
            </>
          ) : (
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item label="Permission Code" name="code">
                  <Input disabled style={{ fontFamily: "monospace", background: "#fafafa" }} />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  label="Permission Name"
                  name="name"
                  rules={[{ required: true, message: "Please enter a name" }]}
                >
                  <Input placeholder="e.g., View Talents" />
                </Form.Item>
              </Col>
            </Row>
          )}
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                label="Module Display Name"
                name="module"
                rules={[{ required: true, message: "Please enter a module" }]}
              >
                <Input placeholder="e.g., Talent Management" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                label="Parent Role"
                name="parentRole"
                initialValue="All"
              >
                <Select>
                  <Option value="Customer">Customer</Option>
                  <Option value="Partner">Partner</Option>
                  <Option value="Admin">Admin</Option>
                  <Option value="All">All (Shared)</Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>
          <Form.Item label="Description" name="description">
            <Input.TextArea rows={2} placeholder="Brief description of what this permission allows" />
          </Form.Item>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item label="Sort Order" name="sortOrder" initialValue={0}>
                <Input type="number" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="isActive" valuePropName="checked" initialValue={true}>
                <Checkbox>Active</Checkbox>
              </Form.Item>
            </Col>
          </Row>
          <Form.Item style={{ marginTop: 16, textAlign: "right" }}>
            <Space>
              <Button onClick={handleClosePermissionModal}>Cancel</Button>
              <Button type="primary" htmlType="submit" loading={saving} style={{ backgroundColor: "#00d9a9", borderColor: "#00d9a9" }}>
                {editingPermission ? "Update" : "Create"}
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title={editingProfileRole ? "Edit Profile Role" : "Add New Profile Role"}
        open={isProfileRoleModalVisible}
        onCancel={handleCloseProfileRoleModal}
        footer={null}
        width={800}
      >
        <Form form={profileRoleForm} layout="vertical" onFinish={handleProfileRoleSubmit}>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                label="Role Name"
                name="name"
                rules={[{ required: true, message: "Please enter a role name" }]}
              >
                <Input placeholder="e.g., Talent Finder" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                label="Parent Role"
                name="parentRole"
                rules={[{ required: true, message: "Please select a parent role" }]}
              >
                <Select placeholder="Select parent role" onChange={handleParentRoleChange}>
                  <Option value="Admin">Admin</Option>
                  <Option value="Customer">Customer</Option>
                  <Option value="Partner">Partner</Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>
          <Form.Item label="Description" name="description">
            <Input.TextArea rows={2} placeholder="Describe what this role is for" />
          </Form.Item>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item label="Scope" name="scope" initialValue="global">
                <Select>
                  <Option value="global">Global (Available to all organizations)</Option>
                  <Option value="organization">Organization-specific</Option>
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="isDefault" valuePropName="checked">
                <Checkbox>Set as default for this parent role</Checkbox>
              </Form.Item>
            </Col>
          </Row>
          
          <div style={{ marginBottom: 16 }}>
            <Title level={5}>Permission Sets</Title>
            <Text type="secondary">Select the permissions to include in this profile role</Text>
          </div>
          
          <Form.Item name="selectedPermissions">
            <Checkbox.Group style={{ width: "100%" }}>
              {permissionModules.length > 0 ? (
                <Collapse defaultActiveKey={permissionModules} ghost>
                  {permissionModules.map(module => {
                    const modulePerms = permissions.filter(p => p.module === module);
                    return (
                      <Panel 
                        header={
                          <span style={{ fontWeight: 500, color: "#014C75" }}>
                            {module} ({modulePerms.length})
                          </span>
                        } 
                        key={module}
                      >
                        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
                          {modulePerms.map(perm => (
                            <Checkbox key={perm.code} value={perm.code}>
                              {perm.name}
                            </Checkbox>
                          ))}
                        </div>
                      </Panel>
                    );
                  })}
                </Collapse>
              ) : (
                <Empty description="Select a parent role to see available permissions" />
              )}
            </Checkbox.Group>
          </Form.Item>

          <Form.Item style={{ marginTop: 16, textAlign: "right" }}>
            <Space>
              <Button onClick={handleCloseProfileRoleModal}>Cancel</Button>
              <Button type="primary" htmlType="submit" loading={saving} style={{ backgroundColor: "#00d9a9", borderColor: "#00d9a9" }}>
                {editingProfileRole ? "Update Role" : "Create Role"}
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </RolePermissionWrapper>
  );
};

export default RolePermission;
